import * as indicatorTypes from "@chartLib/lib/indicator";
import * as seriesTypes from "@chartLib/lib/series";
import * as tooltipTypes from "@chartLib/lib/tooltip";
import * as coordinateTypes from "@chartLib/lib/coordinates";
import {
  WindowSizeType,
  StrokeType,
  ThresholdLineType,
  NumericPropertyType,
  ColorType,
  SourcePathType,
  BooleanType,
  HistogramBarType,
  MovingAverageType,
  SelectType,
  OpacityType,
} from "../PropertyTypes";

const oscillatorsRegistry = {
  displayName: "Osciladores",
  indicators: {
    // CMF — Chaikin Money Flow (migrado do Silverlight FML.CMF; a mesma
    // fórmula do calculateChaikinMoneyFlow do ChartIQ). ≠ twiggsMF.
    chaikinMoneyFlow: {
      displayName: "Chaikin Money Flow (CMF)",
      key: "chaikinMoneyFlow",
      type: indicatorTypes.chaikinMoneyFlow,
      series: seriesTypes.ChaikinMoneyFlowSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      separatePanel: true,
      yAxisId: "ChaikinMoneyFlow",
      renderKey: "chaikinMoneyFlow",
      decimalPlaces: 4,
      defaultOptions: [
        WindowSizeType({
          name: "period",
          displayName: "Período",
          defaultValue: 20,
          min: 1,
          max: 1000,
        }),
        // O legado desenha histograma (COLORSTICK) e o ChartIQ desenha linha —
        // os dois modos ficam disponíveis, com o do legado como padrão.
        SelectType({
          name: "displayMode",
          displayName: "Exibição",
          defaultValue: "histogram",
          options: [
            { label: "Histograma", value: "histogram" },
            { label: "Linha", value: "line" },
          ],
        }),
      ],
      defaultOptionsForAppearance: [
        ColorType({
          name: "increasingColor",
          displayName: "Cor Positiva",
          defaultValue: { color: "#00DD00" },
        }),
        ColorType({
          name: "decreasingColor",
          displayName: "Cor Negativa",
          defaultValue: { color: "#FF0000" },
        }),
        StrokeType({
          name: "stroke",
          displayName: "Linha (modo linha)",
          defaultValue: {
            color: "#FFFF00",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeZeroLine",
          displayName: "Linha Zero",
          defaultValue: {
            color: "#666666",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
      ],
      transformAppearanceConfig: (config) => ({
        ...config,
        increasingColor: config.increasingColor?.color || "#00DD00",
        decreasingColor: config.decreasingColor?.color || "#FF0000",
        strokeZeroLine: config.strokeZeroLine || {
          color: "#666666",
          dash: "Solid",
          width: 1,
          opacity: 1,
        },
      }),
      tooltip: tooltipTypes.ChaikinMoneyFlowTooltip,
      description:
        "Chaikin Money Flow: média do fluxo de dinheiro (posição do fechamento no range × volume) dividida pelo volume médio do período. Positivo indica pressão compradora; negativo, vendedora.",
    },
    // AroonOsc — Oscilador Aroon (migrado do Silverlight FML.AroonOsc).
    // O Aroon do legado usa HHVBARS/LLVBARS do FECHAMENTO (o `aroon` do odin
    // usa máxima/mínima) — fórmulas diferentes, por isso chave própria.
    aroonOsc: {
      displayName: "Oscilador Aroon (AroonOsc)",
      key: "aroonOsc",
      type: indicatorTypes.aroonOsc,
      series: seriesTypes.AroonOscSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      separatePanel: true,
      yAxisId: "AroonOsc",
      renderKey: "aroonOsc",
      decimalPlaces: 2,
      defaultOptions: [
        WindowSizeType({
          name: "period",
          displayName: "Período",
          defaultValue: 25,
          min: 1,
          max: 1000,
        }),
      ],
      defaultOptionsForAppearance: [
        // Paleta por índice do engine: [Ref(0)=Yellow, Osc(1)=Red].
        StrokeType({
          name: "stroke",
          displayName: "Oscilador",
          defaultValue: {
            color: "#FF0000",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeZeroLine",
          displayName: "Linha de Referência",
          defaultValue: {
            color: "#FFFF00",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
      ],
      tooltip: tooltipTypes.SingleValueTooltip,
      tooltipProps: {
        yLabel: "AroonOsc",
      },
      // Diferença de dois percentuais 0–100 ⇒ limitado a [-100, 100].
      yExtentKeys: ["aroonOsc"],
      yExtents: (plotData) => {
        const values = plotData
          .map((d) => d.aroonOsc)
          .filter((v) => v !== undefined && v !== null && !isNaN(v));
        if (values.length === 0) return [-100, 100];
        const min = Math.min(...values);
        const max = Math.max(...values);
        const padding = Math.max((max - min) * 0.1, 1);
        return [Math.max(-100, min - padding), Math.min(100, max + padding)];
      },
      description:
        "Oscilador Aroon: diferença entre o Aroon de alta e o de baixa, medindo quantas barras se passaram desde a máxima e a mínima do período. Valores positivos indicam tendência de alta; negativos, de baixa.",
    },
    // MICD — Fec menos Fec Anterior Suavizado (migrado do Silverlight FML.MICD).
    micd: {
      displayName: "Fec menos Fec Anterior Suavizado (MICD)",
      key: "micd",
      type: indicatorTypes.micd,
      series: seriesTypes.MICDSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      separatePanel: true,
      yAxisId: "MICD",
      renderKey: "micd",
      decimalPlaces: 4,
      defaultOptions: [
        NumericPropertyType({
          name: "period",
          displayName: "Suavização do MI (N)",
          min: 1,
          max: 1000,
          defaultValue: 3,
        }),
        NumericPropertyType({
          name: "n1",
          displayName: "Média curta (N1)",
          min: 1,
          max: 1000,
          defaultValue: 10,
        }),
        NumericPropertyType({
          name: "n2",
          displayName: "Média longa (N2)",
          min: 1,
          max: 1000,
          defaultValue: 20,
        }),
      ],
      defaultOptionsForAppearance: [
        // Cores default do engine (paleta por índice): DIF=Yellow, MICD=Red.
        StrokeType({
          name: "strokeDif",
          displayName: "Cor DIF",
          defaultValue: {
            color: "#FFFF00",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeMicd",
          displayName: "Cor MICD",
          defaultValue: {
            color: "#FF0000",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
      ],
      labelConfig: {
        lines: [
          { key: "dif", colorPath: "strokeDif.color" },
          { key: "micd", colorPath: "strokeMicd.color" },
        ],
      },
      tooltip: tooltipTypes.MICDTooltip,
      // Oscilador em unidades de preço, simétrico em torno de zero.
      yExtentKeys: ["micd"],
      yExtents: (plotData) => {
        const values = plotData
          .flatMap((d) => [d.micd?.dif, d.micd?.micd])
          .filter((v) => v !== undefined && v !== null && !isNaN(v));
        if (values.length === 0) return [-1, 1];
        const min = Math.min(...values);
        const max = Math.max(...values);
        const padding = Math.max((max - min) * 0.1, 1e-6);
        return [min - padding, max + padding];
      },
      description:
        "Fec menos Fec Anterior Suavizado: a variação do fechamento (C − fechamento anterior) suavizada e passada por um par de médias, com linha de sinal. O cruzamento das linhas indica mudança de força do movimento.",
    },
    rsi: {
      displayName: "RSI (Relative Strength Index)",
      key: "rsi",
      type: indicatorTypes.rsi,
      series: seriesTypes.RSISeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        WindowSizeType({ defaultValue: 14 }),
        SourcePathType({ defaultValue: "close", allowSeriesSource: true }),
      ],
      defaultOptionsForAppearance: [
        // Use simple StrokeType for main RSI line
        StrokeType({
          name: "stroke",
          displayName: "Cor da Linha RSI",
          defaultValue: {
            color: "#8884d8",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        // Use ThresholdLineType for threshold lines with value and stroke properties
        ThresholdLineType({
          name: "overBought",
          displayName: "Sobrecompra",
          defaultValue: {
            value: 70,
            stroke: { color: "#0000FF", dash: "Dash", width: 1, opacity: 1 },
          },
          min: 50,
          max: 90,
        }),
        ThresholdLineType({
          name: "overSold",
          displayName: "Sobrevenda",
          defaultValue: {
            value: 30,
            stroke: { color: "#FF0000", dash: "Dash", width: 1, opacity: 1 },
          },
          min: 10,
          max: 50,
        }),
        ThresholdLineType({
          name: "middle",
          displayName: "Linha Central",
          defaultValue: {
            value: 50,
            stroke: { color: "#8795A1", dash: "Dash", width: 1, opacity: 1 },
          },
          min: 40,
          max: 60,
        }),
      ],
      // No transformation needed - RSI will handle simple values directly
      tooltip: tooltipTypes.RSITooltip,
      renderKey: "rsi", // This should match the accessor in the indicator
      yExtents: () => [0, 100],
    },
    mfi: {
      displayName: "MFI (Money Flow Index)",
      key: "mfi",
      type: indicatorTypes.mfi,
      series: seriesTypes.RSISeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        WindowSizeType({
          name: "windowSize",
          displayName: "Período",
          defaultValue: 14,
          min: 2,
          max: 100,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Cor da Linha MFI",
          defaultValue: {
            color: "#9C27B0",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        BooleanType({
          name: "showZones",
          displayName: "Mostrar Zonas",
          defaultValue: true,
          trueLabel: "Sim",
          falseLabel: "Não",
        }),
        ThresholdLineType({
          name: "overBought",
          displayName: "Sobrecompra",
          defaultValue: {
            value: 80,
            stroke: { color: "#B8C2CC", dash: "Dash", width: 1, opacity: 1 },
          },
          min: 50,
          max: 95,
        }),
        ThresholdLineType({
          name: "overSold",
          displayName: "Sobrevenda",
          defaultValue: {
            value: 20,
            stroke: { color: "#B8C2CC", dash: "Dash", width: 1, opacity: 1 },
          },
          min: 5,
          max: 50,
        }),
      ],
      tooltip: tooltipTypes.MFITooltip,
      renderKey: "mfi",
      yExtents: () => [0, 100],
    },
    atr: {
      displayName: "Average True Range (ATR)",
      key: "atr",
      type: indicatorTypes.atr,
      series: seriesTypes.LineSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [WindowSizeType({ defaultValue: 14 })],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Cor da Linha",
          defaultValue: {
            color: "#FF5722",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
      ],
      tooltip: tooltipTypes.SingleValueTooltip,
      tooltipProps: {
        yLabel: "ATR",
      },
      // Custom yExtents for ATR to handle simple numeric values
      renderKey: "atr", // This should match the accessor in the indicator
    },
    williamsR: {
      displayName: "Williams %R",
      key: "williamsR",
      type: indicatorTypes.williamsR,
      series: seriesTypes.WilliamsRSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        WindowSizeType({
          name: "period",
          displayName: "Período",
          defaultValue: 14,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Cor da Linha",
          defaultValue: {
            color: "#808080",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        BooleanType({
          name: "showZones",
          displayName: "Mostrar Zonas",
          defaultValue: true,
          trueLabel: "Sim",
          falseLabel: "Não",
        }),
        ThresholdLineType({
          name: "overBought",
          displayName: "Sobrecompra",
          defaultValue: {
            value: -20,
            stroke: { color: "#B8C2CC", dash: "Dash", width: 1, opacity: 1 },
          },
          min: -100,
          max: 0,
        }),
        ThresholdLineType({
          name: "overSold",
          displayName: "Sobrevenda",
          defaultValue: {
            value: -80,
            stroke: { color: "#B8C2CC", dash: "Dash", width: 1, opacity: 1 },
          },
          min: -100,
          max: 0,
        }),
      ],
      tooltip: tooltipTypes.WilliamsRTooltip,
      renderKey: "williamsR",
      yExtents: () => [-100, 0],
      decimalPlaces: 2,
      description:
        "Oscilador Williams %R variando entre -100 e 0, indicando zonas de sobrecompra (-20) e sobrevenda (-80).",
    },
    cci: {
      displayName: "Commodity Channel Index (CCI)",
      key: "cci",
      type: indicatorTypes.cci,
      series: seriesTypes.CCISeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        WindowSizeType({
          defaultValue: 20,
          displayName: "Período",
          min: 2,
          max: 100,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Cor da Linha CCI",
          defaultValue: {
            color: "#FF6B35",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        // Reference lines for overbought/oversold
        ThresholdLineType({
          name: "overBought",
          displayName: "Sobrecompra",
          defaultValue: {
            value: 100,
            stroke: { color: "#B8C2CC", dash: "Dash", width: 1, opacity: 1 },
          },
          min: 50,
          max: 200,
        }),
        ThresholdLineType({
          name: "overSold",
          displayName: "Sobrevenda",
          defaultValue: {
            value: -100,
            stroke: { color: "#B8C2CC", dash: "Dash", width: 1, opacity: 1 },
          },
          min: -200,
          max: -50,
        }),
      ],
      tooltip: tooltipTypes.SingleValueTooltip,
      tooltipProps: {
        yLabel: "CCI",
      },
      renderKey: "cci", // This should match the accessor in the indicator
      yExtents: () => [-300, 300],
    },
    stochastic: {
      displayName: "IFR Estocástico (Stochastic Oscillator)",
      key: "stochastic",
      type: indicatorTypes.stochasticOscillator,
      series: seriesTypes.StochasticSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      labelConfig: {
        lines: [
          { key: "K", colorPath: "stroke.kLine" },
          { key: "D", colorPath: "stroke.dLine" },
        ],
      },
      defaultOptions: [
        WindowSizeType({ defaultValue: 12 }),
        // Caps elevados a 1000 (teto do Silverlight) a pedido de cliente (#368) —
        // o limite era só de UI; o calculator nunca teve teto.
        NumericPropertyType({
          name: "kWindowSize",
          displayName: "%K Suavização",
          min: 1,
          max: 1000,
          defaultValue: 3,
        }),
        NumericPropertyType({
          name: "dWindowSize",
          displayName: "%D Suavização",
          min: 1,
          max: 1000,
          defaultValue: 3,
        }),
      ],
      defaultOptionsForAppearance: [
        ColorType({
          name: "kLine",
          displayName: "%K Cor",
          defaultValue: { color: "#ff7f0e" },
        }),
        ColorType({
          name: "dLine",
          displayName: "%D Cor",
          defaultValue: { color: "#17becf" },
        }),
        ColorType({
          name: "top",
          displayName: "Cor Linha Superior",
          defaultValue: { color: "#37a600" },
        }),
        ColorType({
          name: "middleColor",
          displayName: "Cor Linha Central",
          defaultValue: { color: "#b8ab00" },
        }),
        ColorType({
          name: "bottom",
          displayName: "Cor Linha Inferior",
          defaultValue: { color: "#37a600" },
        }),
        ThresholdLineType({
          name: "overBought",
          displayName: "Sobrecompra",
          defaultValue: {
            value: 80,
            stroke: { color: "#37a600", dash: "Dash", width: 1, opacity: 1 },
          },
          min: 50,
          max: 100,
        }),
        ThresholdLineType({
          name: "overSold",
          displayName: "Sobrevenda",
          defaultValue: {
            value: 20,
            stroke: { color: "#37a600", dash: "Dash", width: 1, opacity: 1 },
          },
          min: 0,
          max: 50,
        }),
        ThresholdLineType({
          name: "middle",
          displayName: "Linha Central",
          defaultValue: {
            value: 50,
            stroke: { color: "#b8ab00", dash: "Dash", width: 1, opacity: 1 },
          },
          min: 40,
          max: 60,
        }),
        OpacityType({
          name: "refLineOpacity",
          displayName: "Opacidade Linhas",
          defaultValue: 0.3,
        }),
      ],
      transformAppearanceConfig: (config) => {
        // Build stroke palette and extract numeric thresholds from ThresholdLineType objects
        const strokeColors = {
          kLine: config.kLine?.color || "#ff7f0e",
          dLine: config.dLine?.color || "#17becf",
          top: config.top?.color || "#37a600",
          middle:
            config.middle && config.middle.stroke
              ? config.middle.stroke.color
              : config.middleColor?.color || "#b8ab00",
          bottom: config.bottom?.color || "#37a600",
        };

        return {
          ...config,
          stroke: strokeColors,
          // For tooltip - it expects appearance.stroke
          appearance: {
            stroke: strokeColors,
          },
          overBought:
            config.overBought && config.overBought.value !== undefined
              ? config.overBought.value
              : config.overBought || 80,
          overSold:
            config.overSold && config.overSold.value !== undefined
              ? config.overSold.value
              : config.overSold || 20,
          middle:
            config.middle && config.middle.value !== undefined
              ? config.middle.value
              : config.middle || 50,
          refLineOpacity: config.refLineOpacity ?? 0.3,
        };
      },
      tooltip: tooltipTypes.StochasticTooltip,
      renderKey: "sto", // This should match the accessor in the indicator
      yExtents: () => [0, 100],
    },
    didi: {
      displayName: "DIDI (Linhas)",
      key: "didi",
      type: indicatorTypes.didi,
      series: seriesTypes.DIDISeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        NumericPropertyType({
          name: "highPeriod",
          displayName: "High",
          min: 1,
          max: 200,
          defaultValue: 3,
        }),
        NumericPropertyType({
          name: "midPeriod",
          displayName: "Center",
          min: 1,
          max: 200,
          defaultValue: 8,
        }),
        NumericPropertyType({
          name: "lowPeriod",
          displayName: "Low",
          min: 1,
          max: 400,
          defaultValue: 20,
        }),
        // Moving Average Type is fixed to simple (SMA) in the calculator; do not expose for configuration
        SourcePathType({ defaultValue: "close" }),
      ],
      defaultOptionsForAppearance: [
        // Names per requirement: Long, Short, Center
        StrokeType({
          name: "longStroke",
          displayName: "Long",
          defaultValue: {
            color: "#ff0000",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "shortStroke",
          displayName: "Short",
          defaultValue: {
            color: "#00ff00",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "centerStroke",
          displayName: "Center",
          defaultValue: { color: "#0000ff", dash: "Dot", width: 1, opacity: 1 },
        }),
      ],
      labelConfig: {
        lines: [
          { key: "short", colorPath: "shortStroke.color" },
          { key: "long", colorPath: "longStroke.color" },
        ],
      },
      tooltip: tooltipTypes.DIDITooltip,
      renderKey: "didi",
    },
    accumDist: {
      displayName: "Acumulação & Distribuição (A/D Chaikin)",
      key: "accumDist",
      type: indicatorTypes.accumDist,
      series: seriesTypes.AccumDistSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      separatePanel: true,
      yAxisId: "AccumDist",
      renderKey: "accumDist",
      decimalPlaces: 0,
      defaultOptions: [
        NumericPropertyType({
          name: "n",
          displayName: "Período (M)",
          min: 1,
          max: 1000,
          defaultValue: 20,
        }),
      ],
      defaultOptionsForAppearance: [
        // Cores default do engine (paleta por índice): AD=Yellow, M=Red.
        StrokeType({
          name: "strokeAd",
          displayName: "Cor A/D",
          defaultValue: {
            color: "#FFFF00",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeM",
          displayName: "Cor Média (M)",
          defaultValue: {
            color: "#FF0000",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
      ],
      labelConfig: {
        lines: [
          { key: "ad", colorPath: "strokeAd.color" },
          { key: "m", colorPath: "strokeM.color" },
        ],
      },
      tooltip: tooltipTypes.AccumDistTooltip,
      description:
        "Acumulação & Distribuição de Chaikin: linha acumulada do fluxo de dinheiro (money-flow volume) e sua média móvel. Avalia entrada/saída de capital no papel.",
    },
    rvi: {
      displayName: "Relative Vigor Index (RVI)",
      key: "rvi",
      type: indicatorTypes.rvi,
      series: seriesTypes.RVISeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        WindowSizeType({
          name: "period",
          displayName: "Período",
          defaultValue: 10,
          min: 2,
          max: 50,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "strokeRVI",
          displayName: "Linha RVI",
          defaultValue: {
            color: "#AAAAAA",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "strokeSignal",
          displayName: "Linha Sinal",
          defaultValue: {
            color: "#FF0000",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "strokeZeroLine",
          displayName: "Linha Zero",
          defaultValue: {
            color: "#666666",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        HistogramBarType({
          name: "barHistogram",
          displayName: "Histograma",
          defaultValue: {
            increasingColor: "#8cc176",
            decreasingColor: "#b82c0c",
            opacity: 0.6,
            width: 1,
          },
        }),
      ],
      labelConfig: {
        lines: [
          { key: "rvi", colorPath: "strokeRVI.color" },
          { key: "signal", colorPath: "strokeSignal.color" },
        ],
      },
      tooltip: tooltipTypes.RVITooltip,
      yAxisId: "RVI",
      separatePanel: true,
      renderKey: "rvi",
      yExtents: (plotData, indicator) => {
        const { RVISeries } = seriesTypes;
        return RVISeries.yExtents(plotData, undefined, plotData, indicator);
      },
      decimalPlaces: 2,
      description:
        "Índice de Vigor Relativo mede a relação entre momentum de fechamento (Close-Open) e range verdadeiro (High-Low), indicando a força dos movimentos de preço.",
    },
    awesomeOscillator: {
      displayName: "Awesome Oscillator",
      key: "awesomeOscillator",
      type: indicatorTypes.awesomeOscillator,
      series: seriesTypes.AwesomeOscillatorSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [],
      defaultOptionsForAppearance: [
        ColorType({
          name: "increasingColor",
          displayName: "Cor Crescente",
          defaultValue: { color: "#00DD00" },
        }),
        ColorType({
          name: "decreasingColor",
          displayName: "Cor Decrescente",
          defaultValue: { color: "#FF0000" },
        }),
        StrokeType({
          name: "strokeZeroLine",
          displayName: "Linha Zero",
          defaultValue: {
            color: "#666666",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
      ],
      transformAppearanceConfig: (config) => {
        return {
          ...config,
          increasingColor: config.increasingColor?.color || "#00DD00",
          decreasingColor: config.decreasingColor?.color || "#FF0000",
          strokeZeroLine: config.strokeZeroLine || {
            color: "#666666",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        };
      },
      tooltip: tooltipTypes.AwesomeOscillatorTooltip,
      yAxisId: "AwesomeOscillator",
      separatePanel: true,
      renderKey: "awesomeOscillator",
      decimalPlaces: 4,
      description:
        "Awesome Oscillator calcula a diferença entre SMA(5) e SMA(34) do ponto médio (HL/2), mostrando barras verdes quando aumenta e vermelhas quando diminui.",
    },
    aroon: {
      displayName: "Aroon",
      key: "aroon",
      type: indicatorTypes.aroon,
      series: seriesTypes.AroonSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        WindowSizeType({
          name: "period",
          displayName: "Período",
          defaultValue: 14,
          min: 2,
          max: 100,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "strokeAroonUp",
          displayName: "Aroon Up",
          defaultValue: {
            color: "#00DD00",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "strokeAroonDown",
          displayName: "Aroon Down",
          defaultValue: {
            color: "#FF0000",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
      ],
      labelConfig: {
        lines: [
          { key: "aroonUp", colorPath: "strokeAroonUp.color" },
          { key: "aroonDown", colorPath: "strokeAroonDown.color" },
        ],
      },
      tooltip: tooltipTypes.AroonTooltip,
      yAxisId: "Aroon",
      separatePanel: true,
      renderKey: "aroon",
      yExtents: (plotData, indicator) => {
        const { AroonSeries } = seriesTypes;
        return AroonSeries.yExtents(plotData, undefined, plotData, indicator);
      },
      decimalPlaces: 2,
      description:
        "Aroon indica a força da tendência medindo quantos períodos se passaram desde o maior high (Aroon Up) e o menor low (Aroon Down) em um período N.",
    },
    relativeVolatility: {
      displayName: "Relative Volatility",
      key: "relativeVolatility",
      type: indicatorTypes.relativeVolatility,
      series: seriesTypes.RelativeVolatilitySeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        SourcePathType({
          name: "sourcePath",
          displayName: "Campo",
          defaultValue: "close",
        }),
        WindowSizeType({
          name: "stdPeriod",
          displayName: "Período STD",
          defaultValue: 10,
          min: 2,
          max: 100,
        }),
        WindowSizeType({
          name: "smoothingPeriod",
          displayName: "Período de Suavização",
          defaultValue: 14,
          min: 2,
          max: 100,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Linha Rel Vol",
          defaultValue: {
            color: "#9C27B0",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        ThresholdLineType({
          name: "overBought",
          displayName: "Sobrecompra",
          defaultValue: {
            value: 70,
            stroke: { color: "#4A90E2", dash: "Dash", width: 1, opacity: 1 },
          },
          min: 50,
          max: 95,
        }),
        ThresholdLineType({
          name: "overSold",
          displayName: "Sobrevenda",
          defaultValue: {
            value: 30,
            stroke: { color: "#E57373", dash: "Dash", width: 1, opacity: 1 },
          },
          min: 5,
          max: 50,
        }),
        ThresholdLineType({
          name: "middle",
          displayName: "Linha Central",
          defaultValue: {
            value: 50,
            stroke: { color: "#8795A1", dash: "Dash", width: 1, opacity: 1 },
          },
          min: 40,
          max: 60,
        }),
      ],
      tooltip: tooltipTypes.RelativeVolatilityTooltip,
      tooltipProps: {
        yLabel: "Rel Vol",
      },
      renderKey: "relativeVolatility",
      yExtents: (plotData, indicator) => {
        const { RelativeVolatilitySeries } = seriesTypes;
        return RelativeVolatilitySeries.yExtents(
          plotData,
          undefined,
          plotData,
          indicator,
        );
      },
      yAxisId: "RelativeVolatility",
      separatePanel: true,
      decimalPlaces: 2,
      description:
        "Relative Volatility mede a volatilidade relativa usando standard deviation, separando ganhos e perdas com base na direção do preço. Valores acima de 70 indicam sobrecompra, abaixo de 30 indicam sobrevenda.",
    },
    vortex: {
      displayName: "Indicador Vortex",
      key: "vortex",
      type: indicatorTypes.vortex,
      series: seriesTypes.VortexSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        WindowSizeType({
          name: "period",
          displayName: "Período",
          defaultValue: 14,
          min: 2,
          max: 100,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "strokePlusVI",
          displayName: "+VI",
          defaultValue: {
            color: "#00FF00",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "strokeMinusVI",
          displayName: "-VI",
          defaultValue: {
            color: "#FF0000",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        ThresholdLineType({
          name: "centerLine",
          displayName: "Linha Central",
          defaultValue: {
            value: 1.0,
            stroke: { color: "#8795A1", dash: "Solid", width: 1, opacity: 1 },
          },
          min: 0.5,
          max: 1.5,
        }),
        ThresholdLineType({
          name: "overBought",
          displayName: "Sobrecompra",
          defaultValue: {
            value: 1.1,
            stroke: { color: "#B8C2CC", dash: "Dash", width: 1, opacity: 1 },
          },
          min: 1.0,
          max: 1.5,
        }),
        ThresholdLineType({
          name: "overSold",
          displayName: "Sobrevenda",
          defaultValue: {
            value: 0.9,
            stroke: { color: "#B8C2CC", dash: "Dash", width: 1, opacity: 1 },
          },
          min: 0.5,
          max: 1.0,
        }),
      ],
      labelConfig: {
        lines: [
          { key: "plusVI", colorPath: "strokePlusVI.color" },
          { key: "minusVI", colorPath: "strokeMinusVI.color" },
        ],
      },
      tooltip: tooltipTypes.VortexTooltip,
      yAxisId: "Vortex",
      separatePanel: true,
      renderKey: "vortex",
      yExtents: (plotData, indicator) => {
        const { VortexSeries } = seriesTypes;
        return VortexSeries.yExtents(plotData, undefined, plotData, indicator);
      },
      decimalPlaces: 1,
      description:
        "Indicador Vortex identifica o início de uma nova tendência ou a continuação de uma tendência existente, comparando movimentos positivos (+VI) e negativos (-VI).",
    },
    kst: {
      displayName: "Pring KST (Know Sure Thing)",
      key: "kst",
      type: indicatorTypes.kst,
      series: seriesTypes.KSTSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        SourcePathType({
          name: "sourcePath",
          displayName: "Campo",
          defaultValue: "close",
        }),
        WindowSizeType({
          name: "roc1",
          displayName: "Período de menor taxa de troca",
          defaultValue: 10,
          min: 1,
          max: 100,
        }),
        WindowSizeType({
          name: "sma1",
          displayName: "Período SMA mais leve",
          defaultValue: 10,
          min: 1,
          max: 100,
        }),
        WindowSizeType({
          name: "roc2",
          displayName: "Período de baixa taxa de troca",
          defaultValue: 15,
          min: 1,
          max: 100,
        }),
        WindowSizeType({
          name: "sma2",
          displayName: "Período SMA baixo",
          defaultValue: 10,
          min: 1,
          max: 100,
        }),
        WindowSizeType({
          name: "roc3",
          displayName: "Período de grande taxa de troca",
          defaultValue: 20,
          min: 1,
          max: 100,
        }),
        WindowSizeType({
          name: "sma3",
          displayName: "Período maior de SMA",
          defaultValue: 10,
          min: 1,
          max: 100,
        }),
        WindowSizeType({
          name: "roc4",
          displayName: "Período de maior taxa de troca",
          defaultValue: 30,
          min: 1,
          max: 100,
        }),
        WindowSizeType({
          name: "sma4",
          displayName: "Período maior de SMA",
          defaultValue: 15,
          min: 1,
          max: 100,
        }),
        WindowSizeType({
          name: "signal",
          displayName: "Período de sinalização",
          defaultValue: 9,
          min: 1,
          max: 50,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "strokeKST",
          displayName: "Linha KST",
          defaultValue: {
            color: "#00DD00",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "strokeSignal",
          displayName: "Linha Sinal",
          defaultValue: {
            color: "#FF0000",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "strokeZeroLine",
          displayName: "Linha Zero",
          defaultValue: {
            color: "#666666",
            dash: "Dash",
            width: 1,
            opacity: 1,
          },
        }),
      ],
      labelConfig: {
        lines: [
          { key: "kst", colorPath: "strokeKST.color" },
          { key: "signal", colorPath: "strokeSignal.color" },
        ],
      },
      tooltip: tooltipTypes.KSTTooltip,
      yAxisId: "KST",
      separatePanel: true,
      renderKey: "kst",
      yExtents: (plotData, indicator) => {
        const { KSTSeries } = seriesTypes;
        return KSTSeries.yExtents(plotData, undefined, plotData, indicator);
      },
      decimalPlaces: 2,
      description:
        "Know Sure Thing (KST) é um oscilador de momentum que combina quatro taxas de mudança suavizadas para identificar ciclos de mercado e reversões de tendência.",
    },
    maDeviation: {
      displayName: "Desvio da Média Móvel",
      key: "maDeviation",
      type: indicatorTypes.maDeviation,
      series: seriesTypes.MADeviationSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        WindowSizeType({
          name: "windowSize",
          displayName: "Período",
          defaultValue: 12,
          min: 2,
          max: 100,
        }),
        SourcePathType({
          name: "sourcePath",
          displayName: "Campo",
          defaultValue: "close",
        }),
        MovingAverageType({
          name: "maType",
          displayName: "Tipo de Média",
          defaultValue: "sma",
        }),
        MovingAverageType({
          name: "mode",
          displayName: "Modo",
          options: [
            { value: "Points", label: "Pontos" },
            { value: "Percent", label: "Percentual" },
          ],
          defaultValue: "Points",
        }),
      ],
      defaultOptionsForAppearance: [
        ColorType({
          name: "increasingColor",
          displayName: "Cor Crescente",
          defaultValue: { color: "#00DD00" },
        }),
        ColorType({
          name: "decreasingColor",
          displayName: "Cor Decrescente",
          defaultValue: { color: "#FF0000" },
        }),
        StrokeType({
          name: "strokeZeroLine",
          displayName: "Linha Zero",
          defaultValue: {
            color: "#666666",
            dash: "Dash",
            width: 1,
            opacity: 1,
          },
        }),
      ],
      tooltip: tooltipTypes.MADeviationTooltip,
      yAxisId: "MADeviation",
      separatePanel: true,
      renderKey: "maDeviation",
      transformAppearanceConfig: (appearanceConfig) => {
        // Extract color strings from ColorType objects
        const transformed = { ...appearanceConfig };
        if (
          transformed.increasingColor &&
          typeof transformed.increasingColor === "object"
        ) {
          transformed.increasingColor = transformed.increasingColor.color;
        }
        if (
          transformed.decreasingColor &&
          typeof transformed.decreasingColor === "object"
        ) {
          transformed.decreasingColor = transformed.decreasingColor.color;
        }
        return transformed;
      },
      yExtents: (plotData, indicator) => {
        if (!plotData || plotData.length === 0) return undefined;

        const values = plotData
          .map((d) => d[indicator.renderKey])
          .filter((d) => d && d.value !== undefined && Number.isFinite(d.value))
          .map((d) => d.value);

        if (values.length === 0) return undefined;

        const min = Math.min(...values);
        const max = Math.max(...values);
        const range = max - min;
        const padding = range * 0.1;

        return [min - padding, max + padding];
      },
      decimalPlaces: 2,
      description:
        "Desvio da Média Móvel (MA Deviation) mostra a diferença entre o preço e sua média móvel, em pontos ou percentual. Barras verdes indicam desvio crescente, barras vermelhas indicam desvio decrescente.",
    },
    bollingerBandwidth: {
      displayName: "Bollinger Bandwidth (Largura)",
      key: "bollingerBandwidth",
      type: indicatorTypes.bollingerBandwidth,
      series: seriesTypes.LineSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        WindowSizeType({
          name: "windowSize",
          displayName: "Período",
          defaultValue: 20,
          min: 2,
          max: 100,
        }),
        NumericPropertyType({
          name: "multiplier",
          displayName: "Desvios Padrão",
          defaultValue: 2,
          min: 0.1,
          max: 5,
          step: 0.1,
        }),
        SourcePathType({
          name: "sourcePath",
          displayName: "Campo",
          defaultValue: "close",
        }),
        MovingAverageType({
          name: "movingAverageType",
          displayName: "Tipo de Média",
          defaultValue: "sma",
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Cor da Linha",
          defaultValue: {
            color: "#2962FF",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
      ],
      tooltip: tooltipTypes.BollingerBandwidthTooltip,
      renderKey: "bollingerBandwidth",
      yExtents: (plotData, indicator) => {
        if (!plotData || plotData.length === 0) return undefined;

        const values = plotData
          .map((d) => d[indicator.renderKey])
          .filter((d) => d !== undefined && Number.isFinite(d));

        if (values.length === 0) return undefined;

        const min = Math.min(...values);
        const max = Math.max(...values);
        const range = max - min;
        const padding = range * 0.1;

        return [Math.max(0, min - padding), max + padding];
      },
      decimalPlaces: 2,
      description:
        "Bollinger Bandwidth mede a largura das Bandas de Bollinger como porcentagem da média móvel. Valores maiores indicam maior volatilidade, valores menores indicam menor volatilidade.",
    },
    bollingerPercentB: {
      displayName: "Bollinger %b",
      key: "bollingerPercentB",
      type: indicatorTypes.bollingerPercentB,
      series: seriesTypes.BollingerPercentBSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        WindowSizeType({
          name: "windowSize",
          displayName: "Período",
          defaultValue: 20,
          min: 2,
          max: 100,
        }),
        SourcePathType({ defaultValue: "close" }),
        NumericPropertyType({
          name: "multiplier",
          displayName: "Desvios Padrão",
          defaultValue: 2,
          min: 0.5,
          max: 5,
          step: 0.1,
        }),
        MovingAverageType({
          name: "movingAverageType",
          displayName: "Tipo de Média Móvel",
          defaultValue: "sma",
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Cor da Linha %b",
          defaultValue: {
            color: "#9C27B0",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        ThresholdLineType({
          name: "overBought",
          displayName: "Limite Superior",
          defaultValue: {
            value: 100,
            stroke: { color: "#0000FF", dash: "Dash", width: 1, opacity: 1 },
          },
          min: 50,
          max: 150,
        }),
        ThresholdLineType({
          name: "overSold",
          displayName: "Limite Inferior",
          defaultValue: {
            value: 0,
            stroke: { color: "#FF0000", dash: "Dash", width: 1, opacity: 1 },
          },
          min: -50,
          max: 50,
        }),
        ThresholdLineType({
          name: "middle",
          displayName: "Linha Central",
          defaultValue: {
            value: 50,
            stroke: { color: "#8795A1", dash: "Dash", width: 1, opacity: 1 },
          },
          min: 0,
          max: 100,
        }),
      ],
      tooltip: tooltipTypes.BollingerPercentBTooltip,
      renderKey: "bollingerPercentB",
      yExtents: () => [-10, 110],
      decimalPlaces: 2,
      description:
        "Bollinger %b indica a posição do preço dentro das Bandas de Bollinger. Valores acima de 100 indicam que o preço está acima da banda superior, valores abaixo de 0 indicam que está abaixo da banda inferior.",
    },
    ravi: {
      displayName: "RAVI (Range Action Verification Index)",
      key: "ravi",
      type: indicatorTypes.ravi,
      series: seriesTypes.MADeviationSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        WindowSizeType({
          name: "shortPeriod",
          displayName: "Período Curto",
          defaultValue: 7,
          min: 1,
          max: 100,
        }),
        WindowSizeType({
          name: "longPeriod",
          displayName: "Período Longo",
          defaultValue: 65,
          min: 2,
          max: 200,
        }),
        SourcePathType({
          name: "sourcePath",
          displayName: "Campo",
          defaultValue: "close",
        }),
        MovingAverageType({
          name: "maType",
          displayName: "Tipo de Média",
          defaultValue: "sma",
        }),
      ],
      defaultOptionsForAppearance: [
        ColorType({
          name: "increasingColor",
          displayName: "Cor Crescente",
          defaultValue: { color: "#00DD00" },
        }),
        ColorType({
          name: "decreasingColor",
          displayName: "Cor Decrescente",
          defaultValue: { color: "#FF0000" },
        }),
        StrokeType({
          name: "strokeZeroLine",
          displayName: "Linha Zero",
          defaultValue: {
            color: "#666666",
            dash: "Dash",
            width: 1,
            opacity: 1,
          },
        }),
      ],
      tooltip: tooltipTypes.RAVITooltip,
      yAxisId: "RAVI",
      separatePanel: true,
      renderKey: "ravi",
      transformAppearanceConfig: (appearanceConfig) => {
        // Extract color strings from ColorType objects
        const transformed = { ...appearanceConfig };
        if (
          transformed.increasingColor &&
          typeof transformed.increasingColor === "object"
        ) {
          transformed.increasingColor = transformed.increasingColor.color;
        }
        if (
          transformed.decreasingColor &&
          typeof transformed.decreasingColor === "object"
        ) {
          transformed.decreasingColor = transformed.decreasingColor.color;
        }
        return transformed;
      },
      yExtents: (plotData, indicator) => {
        if (!plotData || plotData.length === 0) return undefined;

        const values = plotData
          .map((d) => d[indicator.renderKey])
          .filter((d) => d && d.value !== undefined && Number.isFinite(d.value))
          .map((d) => d.value);

        if (values.length === 0) return undefined;

        const min = Math.min(...values);
        const max = Math.max(...values);
        const range = max - min;
        const padding = range * 0.1;

        return [min - padding, max + padding];
      },
      decimalPlaces: 2,
      description:
        "RAVI (Range Action Verification Index) mede a diferença percentual entre médias móveis de curto e longo prazo. Valores absolutos altos (>±3%) indicam mercado em tendência, valores próximos de zero indicam mercado lateral.",
    },
    schaffTrendCycle: {
      displayName: "Ciclo de Tendência de Schaff (STC)",
      key: "schaffTrendCycle",
      type: indicatorTypes.schaffTrendCycle,
      series: seriesTypes.SchaffTrendCycleSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        WindowSizeType({
          name: "period",
          displayName: "Período Estocástico",
          defaultValue: 10,
          min: 2,
          max: 50,
        }),
        WindowSizeType({
          name: "shortCycle",
          displayName: "Ciclo Curto",
          defaultValue: 23,
          min: 2,
          max: 100,
        }),
        WindowSizeType({
          name: "longCycle",
          displayName: "Ciclo Longo",
          defaultValue: 50,
          min: 2,
          max: 200,
        }),
        SourcePathType({
          name: "sourcePath",
          displayName: "Campo",
          defaultValue: "close",
          allowSeriesSource: true,
        }),
        MovingAverageType({
          name: "movingAverageType",
          displayName: "Tipo de Média Móvel",
          defaultValue: "ema",
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Cor da Linha STC",
          defaultValue: {
            color: "#2196F3",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        BooleanType({
          name: "showZones",
          displayName: "Mostrar Zonas",
          defaultValue: true,
          trueLabel: "Sim",
          falseLabel: "Não",
        }),
        ThresholdLineType({
          name: "overBought",
          displayName: "Sobrecompra",
          defaultValue: {
            value: 75,
            stroke: { color: "#B8C2CC", dash: "Dash", width: 1, opacity: 1 },
          },
          min: 50,
          max: 95,
        }),
        ThresholdLineType({
          name: "overSold",
          displayName: "Sobrevenda",
          defaultValue: {
            value: 25,
            stroke: { color: "#B8C2CC", dash: "Dash", width: 1, opacity: 1 },
          },
          min: 5,
          max: 50,
        }),
      ],
      tooltip: tooltipTypes.SchaffTrendCycleTooltip,
      renderKey: "schaffTrendCycle",
      yExtents: () => [0, 100],
      decimalPlaces: 2,
      description:
        "Schaff Trend Cycle (STC) é um oscilador que combina MACD com duplo estocástico suavizado, oscilando entre 0 e 100. Valores acima de 75 indicam sobrecompra, abaixo de 25 indicam sobrevenda.",
    },
    shinohara: {
      displayName: "Relação de Intensidade Shinohara",
      key: "shinohara",
      type: indicatorTypes.shinohara,
      series: seriesTypes.ShinoharaSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        WindowSizeType({
          name: "period",
          displayName: "Período",
          defaultValue: 26,
          min: 2,
          max: 100,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "strokeStrongRatio",
          displayName: "Linha Razão Forte",
          defaultValue: {
            color: "#E99B54",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "strokeWeakRatio",
          displayName: "Linha Razão Fraca",
          defaultValue: {
            color: "#5F7CB8",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
      ],
      labelConfig: {
        lines: [
          { key: "strongRatio", colorPath: "strokeStrongRatio.color" },
          { key: "weakRatio", colorPath: "strokeWeakRatio.color" },
        ],
      },
      tooltip: tooltipTypes.ShinoharaTooltip,
      renderKey: "shinohara",
      yExtents: (plotData, indicator) => {
        if (!plotData || plotData.length === 0) return undefined;

        const values = [];
        for (const d of plotData) {
          const data = d[indicator.renderKey];
          if (data) {
            if (
              data.strongRatio !== undefined &&
              Number.isFinite(data.strongRatio)
            ) {
              values.push(data.strongRatio);
            }
            if (
              data.weakRatio !== undefined &&
              Number.isFinite(data.weakRatio)
            ) {
              values.push(data.weakRatio);
            }
          }
        }

        if (values.length === 0) return undefined;

        const min = Math.min(...values);
        const max = Math.max(...values);
        const range = max - min;
        const padding = range * 0.1;

        return [min - padding, max + padding];
      },
      decimalPlaces: 2,
      description:
        "Shinohara Intensity Ratio mede a força do mercado através de duas razões: Razão Forte (força interdiária) e Razão Fraca (fraqueza intradiária). Valores acima de 100 indicam pressão vendedora dominante, abaixo de 100 indicam pressão compradora.",
    },
    trendIntensity: {
      displayName: "Índice de Intensidade de Tendência",
      key: "trendIntensity",
      type: indicatorTypes.trendIntensity,
      series: seriesTypes.TrendIntensitySeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        WindowSizeType({
          name: "period",
          displayName: "Período",
          defaultValue: 14,
          min: 2,
          max: 100,
        }),
        SourcePathType({
          name: "sourcePath",
          displayName: "Campo",
          defaultValue: "close",
        }),
        WindowSizeType({
          name: "signalPeriod",
          displayName: "Período do Sinal",
          defaultValue: 9,
          min: 2,
          max: 50,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "strokeTII",
          displayName: "Linha TII",
          defaultValue: {
            color: "#8884d8",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "strokeSignal",
          displayName: "Linha Sinal",
          defaultValue: {
            color: "#FF0000",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        BooleanType({
          name: "showZones",
          displayName: "Mostrar Zonas",
          defaultValue: true,
          trueLabel: "Sim",
          falseLabel: "Não",
        }),
        ThresholdLineType({
          name: "overBought",
          displayName: "Sobrecompra",
          defaultValue: {
            value: 80,
            stroke: { color: "#B8C2CC", dash: "Dash", width: 1, opacity: 1 },
          },
          min: 50,
          max: 95,
        }),
        ThresholdLineType({
          name: "overSold",
          displayName: "Sobrevenda",
          defaultValue: {
            value: 20,
            stroke: { color: "#B8C2CC", dash: "Dash", width: 1, opacity: 1 },
          },
          min: 5,
          max: 50,
        }),
      ],
      labelConfig: {
        lines: [
          { key: "tii", colorPath: "strokeTII.color" },
          { key: "signal", colorPath: "strokeSignal.color" },
        ],
      },
      tooltip: tooltipTypes.TrendIntensityTooltip,
      renderKey: "trendIntensity",
      yExtents: () => [0, 100],
      decimalPlaces: 2,
      description:
        "Índice de Intensidade de Tendência mede a força da tendência comparando o preço com sua média móvel. Valores acima de 80 indicam forte tendência de alta, abaixo de 20 indicam forte tendência de baixa.",
    },
    pmo: {
      displayName: "PMO (Price Momentum Oscillator)",
      key: "pmo",
      type: indicatorTypes.pmo,
      series: seriesTypes.PMOSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        WindowSizeType({
          name: "smoothingPeriod",
          displayName: "Período de Suavização",
          defaultValue: 35,
          min: 2,
          max: 100,
        }),
        WindowSizeType({
          name: "doubleSmoothingPeriod",
          displayName: "Período Dupla Suavização",
          defaultValue: 20,
          min: 2,
          max: 100,
        }),
        WindowSizeType({
          name: "signalPeriod",
          displayName: "Período do Sinal",
          defaultValue: 10,
          min: 2,
          max: 50,
        }),
        SourcePathType({
          name: "sourcePath",
          displayName: "Campo",
          defaultValue: "close",
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "strokePMO",
          displayName: "Linha PMO",
          defaultValue: {
            color: "#2196F3",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "strokeSignal",
          displayName: "Linha Sinal",
          defaultValue: {
            color: "#FF0000",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "strokeZeroLine",
          displayName: "Linha Zero",
          defaultValue: {
            color: "#B8C2CC",
            dash: "Dash",
            width: 1,
            opacity: 0.7,
          },
        }),
        BooleanType({
          name: "showZones",
          displayName: "Mostrar Zonas",
          defaultValue: true,
          trueLabel: "Sim",
          falseLabel: "Não",
        }),
        ThresholdLineType({
          name: "overBought",
          displayName: "Sobrecompra",
          defaultValue: {
            value: 2.5,
            stroke: { color: "#B8C2CC", dash: "Dash", width: 1, opacity: 1 },
          },
          min: 0,
          max: 10,
          step: 0.1,
        }),
        ThresholdLineType({
          name: "overSold",
          displayName: "Sobrevenda",
          defaultValue: {
            value: -2.5,
            stroke: { color: "#B8C2CC", dash: "Dash", width: 1, opacity: 1 },
          },
          min: -10,
          max: 0,
          step: 0.1,
        }),
      ],
      labelConfig: {
        lines: [
          { key: "pmo", colorPath: "strokePMO.color" },
          { key: "signal", colorPath: "strokeSignal.color" },
        ],
      },
      tooltip: tooltipTypes.PMOTooltip,
      renderKey: "pmo",
      decimalPlaces: 3,
      description:
        "Oscilador de Momentum de Preço (PMO) aplica dupla suavização exponencial à taxa de variação do preço, fornecendo sinais suavizados de momentum. Oscila em torno de zero com zonas de sobrecompra/sobrevenda em ±2.5.",
    },
    ultimateOscillator: {
      displayName: "Derradeiro Oscilador (Ultimate Oscillator)",
      key: "ultimateOscillator",
      type: indicatorTypes.ultimateOscillator,
      series: seriesTypes.UltimateOscillatorSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        WindowSizeType({
          name: "cycle1",
          displayName: "Ciclo 1",
          defaultValue: 7,
          min: 2,
          max: 100,
        }),
        WindowSizeType({
          name: "cycle2",
          displayName: "Ciclo 2",
          defaultValue: 14,
          min: 2,
          max: 100,
        }),
        WindowSizeType({
          name: "cycle3",
          displayName: "Ciclo 3",
          defaultValue: 28,
          min: 2,
          max: 100,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Linha",
          defaultValue: {
            color: "#9C27B0",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        BooleanType({
          name: "showZones",
          displayName: "Mostrar Zonas",
          defaultValue: true,
          trueLabel: "Sim",
          falseLabel: "Não",
        }),
        ThresholdLineType({
          name: "overBought",
          displayName: "Sobrecompra",
          defaultValue: {
            value: 70,
            stroke: { color: "#0000FF", dash: "Dash", width: 1, opacity: 1 },
          },
          min: 0,
          max: 100,
          step: 1,
        }),
        ThresholdLineType({
          name: "overSold",
          displayName: "Sobrevenda",
          defaultValue: {
            value: 30,
            stroke: { color: "#FF0000", dash: "Dash", width: 1, opacity: 1 },
          },
          min: 0,
          max: 100,
          step: 1,
        }),
      ],
      tooltip: tooltipTypes.UltimateOscillatorTooltip,
      renderKey: "ultimateOscillator",
      yExtents: () => [0, 100],
      decimalPlaces: 2,
      description:
        "Derradeiro Oscilador (Ultimate Oscillator) combina três períodos de tempo diferentes (7, 14, 28) para identificar condições de sobrecompra (acima de 70) e sobrevenda (abaixo de 30). Oscila entre 0 e 100.",
    },
    stochasticMomentum: {
      displayName: "Índice Momentum Estocástico",
      key: "stochasticMomentum",
      type: indicatorTypes.stochasticMomentum,
      series: seriesTypes.StochasticMomentumSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        // Caps elevados a 1000 (teto do Silverlight) a pedido de cliente (#368) —
        // o limite era só de UI; o calculator nunca teve teto.
        WindowSizeType({
          name: "kPeriods",
          displayName: "%K Períodos",
          defaultValue: 10,
          min: 2,
          max: 1000,
        }),
        WindowSizeType({
          name: "kSmoothPeriods",
          displayName: "%K Suavização",
          defaultValue: 3,
          min: 1,
          max: 1000,
        }),
        WindowSizeType({
          name: "kDoubleSmoothPeriods",
          displayName: "%K Suavização Dupla",
          defaultValue: 3,
          min: 1,
          max: 1000,
        }),
        WindowSizeType({
          name: "dPeriods",
          displayName: "%D Períodos",
          defaultValue: 10,
          min: 1,
          max: 1000,
        }),
        MovingAverageType({
          name: "dMaType",
          displayName: "Tipo de Média %D",
          defaultValue: "ema",
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "strokeK",
          displayName: "Linha %K",
          defaultValue: {
            color: "#8884d8",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "strokeD",
          displayName: "Linha %D",
          defaultValue: {
            color: "#FF0000",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        BooleanType({
          name: "showZones",
          displayName: "Mostrar Zonas",
          defaultValue: true,
          trueLabel: "Sim",
          falseLabel: "Não",
        }),
        ThresholdLineType({
          name: "overBought",
          displayName: "Sobrecompra",
          defaultValue: {
            value: 40,
            stroke: { color: "#B8C2CC", dash: "Dash", width: 1, opacity: 1 },
          },
          min: 20,
          max: 80,
        }),
        ThresholdLineType({
          name: "overSold",
          displayName: "Sobrevenda",
          defaultValue: {
            value: -40,
            stroke: { color: "#B8C2CC", dash: "Dash", width: 1, opacity: 1 },
          },
          min: -80,
          max: -20,
        }),
        ThresholdLineType({
          name: "zeroLine",
          displayName: "Linha Zero",
          defaultValue: {
            value: 0,
            stroke: { color: "#8795A1", dash: "Solid", width: 1, opacity: 1 },
          },
          min: 0,
          max: 0,
        }),
      ],
      labelConfig: {
        lines: [
          { key: "k", colorPath: "strokeK.color" },
          { key: "d", colorPath: "strokeD.color" },
        ],
      },
      tooltip: tooltipTypes.StochasticMomentumTooltip,
      renderKey: "stochasticMomentum",
      yExtents: (plotData, indicator) => {
        if (!plotData || plotData.length === 0) return [-60, 60];

        const values = [];
        for (const d of plotData) {
          const data = d[indicator.renderKey];
          if (data) {
            if (data.k !== undefined && Number.isFinite(data.k)) {
              values.push(data.k);
            }
            if (data.d !== undefined && Number.isFinite(data.d)) {
              values.push(data.d);
            }
          }
        }

        if (values.length === 0) return [-60, 60];

        const min = Math.min(...values, -40);
        const max = Math.max(...values, 40);
        const range = max - min;
        const padding = range * 0.1;

        return [min - padding, max + padding];
      },
      decimalPlaces: 2,
      description:
        "Índice Momentum Estocástico (SMI) é um oscilador que mede a posição do preço de fechamento em relação ao ponto médio do range de high/low, com dupla suavização exponencial. Valores acima de 40 indicam sobrecompra, abaixo de -40 indicam sobrevenda.",
    },
    disparity: {
      displayName: "Índice de Disparidade (Disparity Index)",
      key: "disparity",
      type: indicatorTypes.disparity,
      series: seriesTypes.DisparitySeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        WindowSizeType({
          name: "windowSize",
          displayName: "Período",
          defaultValue: 14,
          min: 2,
          max: 100,
        }),
        SourcePathType({
          name: "sourcePath",
          displayName: "Campo",
          defaultValue: "close",
          allowSeriesSource: true,
        }),
        MovingAverageType({
          name: "movingAverageType",
          displayName: "Tipo de Média Móvel",
          defaultValue: "sma",
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Cor da Linha",
          defaultValue: {
            color: "#2962FF",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        ThresholdLineType({
          name: "zeroLine",
          displayName: "Linha Zero",
          defaultValue: {
            value: 0,
            stroke: { color: "#8795A1", dash: "Solid", width: 1, opacity: 1 },
          },
          min: 0,
          max: 0,
        }),
      ],
      tooltip: tooltipTypes.DisparityTooltip,
      renderKey: "disparity",
      yExtents: (plotData, indicator) => {
        if (!plotData || plotData.length === 0) return [-10, 10];

        const values = plotData
          .map((d) => d[indicator.renderKey])
          .filter((d) => d !== undefined && Number.isFinite(d));

        if (values.length === 0) return [-10, 10];

        const min = Math.min(...values);
        const max = Math.max(...values);
        const range = max - min;
        const padding = range * 0.1;

        return [min - padding, max + padding];
      },
      decimalPlaces: 2,
      description:
        "Índice de Disparidade (Disparity Index) mostra a diferença percentual entre o preço e sua média móvel. Valores positivos indicam que o preço está acima da média (alta), valores negativos indicam que o preço está abaixo (baixa). Útil para identificar condições de sobrecompra/sobrevenda.",
    },
    priceOscillator: {
      displayName: "Oscilador de Preço (Price Oscillator)",
      key: "priceOscillator",
      type: indicatorTypes.priceOscillator,
      series: seriesTypes.DisparitySeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        WindowSizeType({
          name: "shortPeriod",
          displayName: "Período Curto",
          defaultValue: 12,
          min: 2,
          max: 100,
        }),
        WindowSizeType({
          name: "longPeriod",
          displayName: "Período Longo",
          defaultValue: 26,
          min: 2,
          max: 200,
        }),
        SourcePathType({
          name: "sourcePath",
          displayName: "Campo",
          defaultValue: "close",
          allowSeriesSource: true,
        }),
        MovingAverageType({
          name: "movingAverageType",
          displayName: "Tipo de Média Móvel",
          defaultValue: "ema",
        }),
        SelectType({
          name: "mode",
          displayName: "Modo",
          options: [
            { value: "Points", label: "Pontos" },
            { value: "Percent", label: "Percentual" },
          ],
          defaultValue: "Points",
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Cor da Linha",
          defaultValue: {
            color: "#2962FF",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        ThresholdLineType({
          name: "zeroLine",
          displayName: "Linha Zero",
          defaultValue: {
            value: 0,
            stroke: { color: "#8795A1", dash: "Solid", width: 1, opacity: 1 },
          },
          min: 0,
          max: 0,
        }),
      ],
      tooltip: tooltipTypes.PriceOscillatorTooltip,
      renderKey: "priceOscillator",
      yExtents: (plotData, indicator) => {
        if (!plotData || plotData.length === 0) return [-10, 10];

        const values = plotData
          .map((d) => d[indicator.renderKey])
          .filter((d) => d !== undefined && Number.isFinite(d));

        if (values.length === 0) return [-10, 10];

        const min = Math.min(...values);
        const max = Math.max(...values);
        const range = max - min;
        const padding = range * 0.1;

        return [min - padding, max + padding];
      },
      decimalPlaces: 2,
      description:
        "Oscilador de Preço (Price Oscillator) mostra a diferença entre duas médias móveis. Modo Pontos (padrão): MA curta - MA longa. Modo Percentual: 100×((MA curta/MA longa)-1). Similar ao MACD mas permite escolher qualquer tipo de média móvel.",
    },
    psy: {
      displayName: "PSY (Linha Psicológica)",
      key: "psy",
      type: indicatorTypes.psy,
      series: seriesTypes.LineSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        WindowSizeType({
          name: "windowSize",
          displayName: "Período",
          defaultValue: 20,
          min: 5,
          max: 100,
        }),
        SourcePathType({
          name: "sourcePath",
          displayName: "Campo",
          defaultValue: "close",
          allowSeriesSource: true,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Cor da Linha PSY",
          defaultValue: {
            color: "#FF6B6B",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
      ],
      tooltip: tooltipTypes.PsyTooltip,
      renderKey: "psy",
      yExtents: () => [0, 100],
      decimalPlaces: 2,
      description:
        "PSY (Linha Psicológica) mede o sentimento do mercado baseado na porcentagem de barras que fecharam acima da barra anterior. Valores próximos a 100 indicam forte sentimento de alta, enquanto valores próximos a 0 indicam forte sentimento de baixa.",
    },
    intradayMomentum: {
      displayName: "IMI (Índice de Momentum Intradiário)",
      key: "intradayMomentum",
      type: indicatorTypes.intradayMomentum,
      series: seriesTypes.IntradayMomentumSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        WindowSizeType({
          name: "windowSize",
          displayName: "Período",
          defaultValue: 20,
          min: 5,
          max: 100,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Cor da Linha IMI",
          defaultValue: {
            color: "#00BCD4",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        ThresholdLineType({
          name: "overBought",
          displayName: "Sobrecompra",
          defaultValue: {
            value: 70,
            stroke: { color: "#0000FF", dash: "Dash", width: 1, opacity: 1 },
          },
          min: 50,
          max: 90,
        }),
        ThresholdLineType({
          name: "overSold",
          displayName: "Sobrevenda",
          defaultValue: {
            value: 30,
            stroke: { color: "#FF0000", dash: "Dash", width: 1, opacity: 1 },
          },
          min: 10,
          max: 50,
        }),
      ],
      tooltip: tooltipTypes.IntradayMomentumTooltip,
      renderKey: "intradayMomentum",
      yExtents: () => [0, 100],
      decimalPlaces: 2,
      description:
        "IMI (Índice de Momentum Intradiário) mede a pressão de compra/venda usando o movimento intradiário (Close - Open). Valores acima de 70 indicam sobrecompra, abaixo de 30 indicam sobrevenda. Similar ao RSI mas usa momentum intradiário ao invés de momentum entre fechamentos.",
    },
    balanceOfPower: {
      displayName: "BOP (Balanço de Poder / Balance of Power)",
      key: "balanceOfPower",
      type: indicatorTypes.balanceOfPower,
      series: seriesTypes.DisparitySeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      separatePanel: true,
      yAxisId: "BalanceOfPower",
      isPriceAxis: false,
      defaultOptions: [
        WindowSizeType({
          name: "windowSize",
          displayName: "Período",
          defaultValue: 14,
          min: 2,
          max: 100,
        }),
        MovingAverageType({
          name: "movingAverageType",
          displayName: "Tipo de Média",
          defaultValue: "sma",
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Cor da Linha",
          defaultValue: {
            color: "#2962FF",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        ThresholdLineType({
          name: "zeroLine",
          displayName: "Linha Zero",
          defaultValue: {
            value: 0,
            stroke: { color: "#8795A1", dash: "Solid", width: 1, opacity: 0.5 },
          },
          hideValue: true,
        }),
      ],
      tooltip: tooltipTypes.BalanceOfPowerTooltip,
      renderKey: "balanceOfPower",
      yExtents: (plotData) => {
        const values = plotData
          .map((d) => d.balanceOfPower)
          .filter((v) => v !== undefined && v !== null);
        if (values.length === 0) return [-1, 1];
        const min = Math.min(...values, -0.1);
        const max = Math.max(...values, 0.1);
        const padding = (max - min) * 0.1 || 0.1;
        return [min - padding, max + padding];
      },
      decimalPlaces: 3,
      description:
        "BOP (Balanço de Poder) mede o equilíbrio entre compradores e vendedores. Calcula a razão (Close - Open) / (High - Low) e aplica uma média móvel. Valores positivos indicam pressão compradora, valores negativos indicam pressão vendedora. Oscila entre -1 e 1, com 0 indicando equilíbrio.",
    },
    ehlerFisher: {
      displayName: "Ehler Fisher (Transformação Ehler Fisher)",
      key: "ehlerFisher",
      type: indicatorTypes.ehlerFisher,
      series: seriesTypes.EhlerFisherSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        WindowSizeType({
          name: "period",
          displayName: "Período",
          defaultValue: 10,
          min: 2,
          max: 100,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "strokeEF",
          displayName: "Linha EF",
          defaultValue: {
            color: "#2196F3",
            dash: "Solid",
            width: 2,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "strokeTrigger",
          displayName: "Linha Trigger",
          defaultValue: {
            color: "#FF0000",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "strokeZeroLine",
          displayName: "Linha Zero",
          defaultValue: {
            color: "#B8C2CC",
            dash: "ShortDash",
            width: 1,
            opacity: 0.7,
          },
        }),
      ],
      labelConfig: {
        lines: [
          { key: "ef", colorPath: "strokeEF.color" },
          { key: "trigger", colorPath: "strokeTrigger.color" },
        ],
      },
      tooltip: tooltipTypes.EhlerFisherTooltip,
      renderKey: "ehlerFisher",
      yAxisId: "EhlerFisher",
      separatePanel: true,
      yExtents: (plotData) => {
        const values = plotData
          .map((d) => d.ehlerFisher)
          .filter((v) => v !== undefined && v !== null)
          .flatMap((v) => [v.ef, v.trigger])
          .filter((v) => v !== undefined && v !== null && !isNaN(v));

        if (values.length === 0) return [-3, 3];

        const min = Math.min(...values);
        const max = Math.max(...values);
        const padding = Math.max((max - min) * 0.2, 0.5);

        return [min - padding, max + padding];
      },
      decimalPlaces: 3,
      description:
        "Transformação Ehler Fisher converte preços em distribuição normal Gaussiana, facilitando identificação de pontos de reversão. Valores extremos (acima de +2 ou abaixo de -2) indicam potenciais reversões. A linha Trigger (EF anterior) auxilia na confirmação de sinais.",
    },
    chandeMomentum: {
      displayName: "CMO (Chande Momentum Oscillator)",
      key: "chandeMomentum",
      type: indicatorTypes.chandeMomentum,
      series: seriesTypes.ChandeMomentumSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        WindowSizeType({
          name: "period",
          displayName: "Período",
          defaultValue: 9,
          min: 2,
          max: 100,
        }),
        SourcePathType({
          name: "sourcePath",
          displayName: "Campo",
          defaultValue: "close",
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Linha CMO",
          defaultValue: {
            color: "#2196F3",
            dash: "Solid",
            width: 2,
            opacity: 1,
          },
        }),
        ThresholdLineType({
          name: "overBought",
          displayName: "Sobrecompra",
          defaultValue: {
            value: 50,
            stroke: { color: "#0000FF", dash: "Dash", width: 1, opacity: 1 },
          },
          min: 30,
          max: 80,
        }),
        ThresholdLineType({
          name: "overSold",
          displayName: "Sobrevenda",
          defaultValue: {
            value: -50,
            stroke: { color: "#FF0000", dash: "Dash", width: 1, opacity: 1 },
          },
          min: -80,
          max: -30,
        }),
        StrokeType({
          name: "zeroLine",
          displayName: "Linha Zero",
          defaultValue: {
            color: "#8795A1",
            dash: "Dash",
            width: 1,
            opacity: 0.5,
          },
        }),
      ],
      tooltip: tooltipTypes.ChandeMomentumTooltip,
      renderKey: "chandeMomentum",
      yAxisId: "ChandeMomentum",
      separatePanel: true,
      yExtents: () => [-100, 100],
      decimalPlaces: 2,
      description:
        "Oscilador de Momentum Chande (CMO) mede momentum usando a razão entre soma de diferenças de preço e soma absoluta das diferenças. Range -100 a +100. Valores acima de +50 indicam sobrecompra, abaixo de -50 sobrevenda. Desenvolvido por Tushar Chande.",
    },
    twiggsMF: {
      displayName: "TMF (Twiggs Money Flow / Fluxo Monetário Twiggs)",
      key: "twiggsMF",
      type: indicatorTypes.twiggsMF,
      series: seriesTypes.TwiggsMFSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        WindowSizeType({
          name: "period",
          defaultValue: 21,
          displayName: "Período",
          min: 2,
          max: 100,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Linha TMF",
          defaultValue: {
            color: "#2196F3",
            dash: "Solid",
            width: 2,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "strokeZeroLine",
          displayName: "Linha Zero",
          defaultValue: {
            color: "#8795A1",
            dash: "Solid",
            width: 1,
            opacity: 0.5,
          },
        }),
      ],
      tooltip: tooltipTypes.TwiggsMFTooltip,
      renderKey: "twiggsMF",
      yAxisId: "TwiggsMF",
      separatePanel: true,
      decimalPlaces: 4,
      description:
        "Twiggs Money Flow (TMF) mede pressão de compra/venda usando volume e true range. Valores positivos indicam acumulação (compra), negativos indicam distribuição (venda). Usa suavização exponencial do Money Flow Volume dividido pelo Volume.",
    },
    swingIndex: {
      displayName: "Swing Index (Índice de oscilação)",
      key: "swingIndex",
      type: indicatorTypes.swingIndex,
      series: seriesTypes.SwingIndexSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        NumericPropertyType({
          name: "limitMoveValue",
          displayName: "Limite de Movimento",
          defaultValue: 0.5,
          min: 0.01,
          max: 10,
          step: 0.1,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Linha Swing Index",
          defaultValue: {
            color: "#2196F3",
            dash: "Solid",
            width: 2,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "strokeZeroLine",
          displayName: "Linha Zero",
          defaultValue: {
            color: "#8795A1",
            dash: "Solid",
            width: 1,
            opacity: 0.5,
          },
        }),
      ],
      tooltip: tooltipTypes.SwingIndexTooltip,
      renderKey: "swingIndex",
      yAxisId: "SwingIndex",
      separatePanel: true,
      decimalPlaces: 2,
      description:
        "Swing Index mede momentum intradiário comparando preço atual com anterior, ajustado pelo range verdadeiro e limite de movimento. Desenvolvido por Welles Wilder. Oscila em torno de zero, valores positivos indicam momentum de alta, negativos indicam momentum de baixa.",
    },
  },
};

export default oscillatorsRegistry;
